import { useMemo } from 'react';
import { useList } from '@/core/store';
import { deviceZone, isValidZone, MS_MIN, zoneOffsetMs } from '@/core/time';
import type { SleepSession } from '@/core/types';
import { peekWorldCache, useWorldVersion } from '@/modules/activity/trace';
import { peekTrips, useDayZones, useTripsVersion } from '@/modules/connections/trips';
import { useProfile } from '@/modules/health/profile';
import {
  bedMinute,
  bodyClock,
  dayAt,
  foldShift,
  minuteOfDay,
  type NightClock,
  nightClock,
  tracePoints,
  type ZoneSpan,
} from './zones';

// Nights: the sleep sessions of each morning, with the clock each was slept on (zones.ts) and
// what travel did to it. Everything here is derived; nothing is stored.

export interface Night {
  /** the morning it belongs to, on the clock you woke up on */
  day: string;
  main: SleepSession;
  /** naps and extra sessions filed under the same morning */
  others: SleepSession[];
  clock: NightClock;
  /** real time asleep, all sessions that morning (seconds) */
  totalS: number;
  /** bedtime as minutes from local midnight (evenings negative), wake-up 0–1439 */
  bedMin: number;
  wakeMin: number;
  /** the same on your body clock (where your body is still living, see zones.ts bodyClock) */
  bodyBedMin: number;
  bodyWakeMin: number;
  /** the same on the home clock; only when a home zone is set */
  homeBedMin?: number;
  homeWakeMin?: number;
  /** local clock minus home clock at bedtime (minutes): +540 = 9 hours ahead of home; 0 without a home */
  awayMin: number;
  /** how far the clock jumped since the previous night (minutes, folded to ±12h); 0 = none */
  jumpMin: number;
  /** real hours between the previous local midnight and this one when the clock jumped (24 − jump) */
  dayLengthH?: number;
  /** real hours awake between the previous night's wake-up and this bedtime */
  awakeBeforeH?: number;
  /** clocks changed while asleep (a flight): end clock minus start clock (minutes) */
  inSleepShiftMin: number;
  /** what the wall clocks say you spent in bed, which differs from real time when they moved */
  wallInBedS: number;
  /** wall clock minus body clock (minutes): + body behind (went east), − body ahead (went west) */
  lagMin: number;
}

/** Sessions that count as sleep (Oura's "rest" is lying down awake). */
const isSleep = (s: SleepSession) => !s.deleted && s.type !== 'rest';

export interface WhereSources {
  trace: ReturnType<typeof peekWorldCache>;
  trips: ZoneSpan[];
  /** where you said you were: Oura's day ("YYYY-MM-DD") → IANA zone */
  told: Record<string, string>;
}

export function buildNights(
  sessions: SleepSession[],
  where: WhereSources,
  homeSetting: string | undefined,
): { nights: Night[]; home: string | null } {
  const points = tracePoints(where.trace);
  // No home unless you set one: plenty of people don't have one. Unknown nights without even
  // an Oura offset are then drawn on this device's zone.
  const home = isValidZone(homeSetting) ? homeSetting : null;
  const provisional = home ?? deviceZone();
  const live = sessions.filter((s) => isSleep(s) && s.source !== 'import');
  // A SmartCalorie night stands in only until Oura's own session for it has synced.
  const overlaps = (s: SleepSession) => live.some((o) => o.start < s.end && s.start < o.end);
  const sorted = sessions
    .filter((s) => isSleep(s) && (s.source !== 'import' || !overlaps(s)))
    .sort((a, b) => a.start - b.start);
  const clocks = sorted.map((s) =>
    nightClock(
      s,
      { trace: points, trips: where.trips, told: where.told[s.day], home },
      provisional,
    ),
  );
  const clockOf = new Map(sorted.map((s, i) => [s.id, clocks[i]!]));

  // Group by the morning on the corrected clock.
  const byDay = new Map<string, SleepSession[]>();
  for (const s of sorted) {
    const c = clockOf.get(s.id)!;
    const day = dayAt(s.end, c.endOff);
    const list = byDay.get(day) ?? [];
    list.push(s);
    byDay.set(day, list);
  }
  const days = [...byDay.keys()].sort();
  const base = days.map((day) => {
    const list = byDay.get(day)!;
    const main =
      [...list]
        .filter((s) => s.type === 'long_sleep')
        .sort((a, b) => (b.totalS ?? 0) - (a.totalS ?? 0))[0] ??
      [...list].sort((a, b) => (b.totalS ?? 0) - (a.totalS ?? 0))[0]!;
    return { day, main, others: list.filter((s) => s !== main), clock: clockOf.get(main.id)! };
  });

  // The body starts on home's clock, or on the first known night's. Unknown nights don't move
  // it (their clock is only a placeholder).
  const firstKnown = base.find((b) => b.clock.source !== 'unknown') ?? base[0];
  const startOff = home
    ? Math.round(zoneOffsetMs(home, base[0]?.main.start ?? Date.now()) / MS_MIN)
    : (firstKnown?.clock.startOff ?? 0);
  let known = startOff;
  const body = bodyClock(
    base.map((b) => {
      if (b.clock.source !== 'unknown') known = b.clock.startOff;
      return { at: b.main.start, off: known };
    }),
    startOff,
  );

  const nights = base.map((b, i): Night => {
    const { main, clock } = b;
    const homeStart = home ? Math.round(zoneOffsetMs(home, main.start) / MS_MIN) : undefined;
    const homeEnd = home ? Math.round(zoneOffsetMs(home, main.end) / MS_MIN) : undefined;
    const bodyOff = Math.round(body[i]!.bodyOff);
    const prev = base[i - 1];
    // A jump only counts between nights whose clocks are known.
    const jump =
      prev && clock.source !== 'unknown' && prev.clock.source !== 'unknown'
        ? foldShift(clock.startOff - prev.clock.endOff)
        : 0;
    const wallInBed = (main.end - main.start) / 1000 + (clock.endOff - clock.startOff) * 60;
    return {
      day: b.day,
      main,
      others: b.others,
      clock,
      totalS: [main, ...b.others].reduce((s, x) => s + (x.totalS ?? 0), 0),
      bedMin: bedMinute(main.start, clock.startOff),
      wakeMin: minuteOfDay(main.end, clock.endOff),
      bodyBedMin: bedMinute(main.start, bodyOff),
      bodyWakeMin: minuteOfDay(main.end, bodyOff),
      homeBedMin: homeStart !== undefined ? bedMinute(main.start, homeStart) : undefined,
      homeWakeMin: homeEnd !== undefined ? minuteOfDay(main.end, homeEnd) : undefined,
      awayMin: homeStart !== undefined ? foldShift(clock.startOff - homeStart) : 0,
      jumpMin: jump,
      dayLengthH: jump ? 24 - jump / 60 : undefined,
      awakeBeforeH: prev ? (main.start - prev.main.end) / 3_600_000 : undefined,
      inSleepShiftMin: foldShift(clock.endOff - clock.startOff),
      wallInBedS: wallInBed,
      lagMin: body[i]!.lagMin,
    };
  });
  return { nights, home };
}

/** Every night, newest last; re-derived when sessions, Trace, Trips App, your answers or home change. */
/** Where a night's bed/wake times are read in the views. */
export type ClockMode = 'local' | 'body' | 'home';

/** Bed and wake minutes of a night on the chosen clock (home falls back to the body clock). */
export function timesOn(n: Night, mode: ClockMode): { bed: number; wake: number } {
  if (mode === 'home' && n.homeBedMin !== undefined && n.homeWakeMin !== undefined)
    return { bed: n.homeBedMin, wake: n.homeWakeMin };
  if (mode === 'local') return { bed: n.bedMin, wake: n.wakeMin };
  return { bed: n.bodyBedMin, wake: n.bodyWakeMin };
}

export function useNights(): { nights: Night[]; home: string | null } {
  const sessions = useList('sleep');
  const traceVersion = useWorldVersion();
  const tripsVersion = useTripsVersion();
  const [told] = useDayZones();
  const [profile] = useProfile();
  // biome-ignore lint/correctness/useExhaustiveDependencies: the versions mark new caches
  return useMemo(
    () =>
      buildNights(
        sessions,
        { trace: peekWorldCache(), trips: peekTrips(), told },
        profile.homeZone,
      ),
    [sessions, traceVersion, tripsVersion, told, profile.homeZone],
  );
}

/** "23:40" from minutes after midnight (negative = the evening before). */
export function clockText(min: number, h24 = false): string {
  const m = ((Math.round(min) % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = String(m % 60).padStart(2, '0');
  if (h24) return `${String(h).padStart(2, '0')}:${mm}`;
  return `${h % 12 || 12}:${mm} ${h < 12 ? 'am' : 'pm'}`;
}

/** "+9h", "−7h", "+5:30" */
export function shiftText(min: number): string {
  const r = Math.round(min / 15) * 15;
  if (!r) return '0h';
  const a = Math.abs(r);
  const h = Math.floor(a / 60);
  return `${r > 0 ? '+' : '−'}${h}${a % 60 ? `:${String(a % 60).padStart(2, '0')}` : 'h'}`;
}

export function average(nums: number[]): number | undefined {
  return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : undefined;
}
